
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "@/hooks/useProject";
import { useJotFormForms } from "@/hooks/useJotFormForms";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface CreateTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateTemplateDialog({ open, onOpenChange }: CreateTemplateDialogProps) {
  const [name, setName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [canvaTemplateUrl, setCanvaTemplateUrl] = useState("");
  const [selectedFormId, setSelectedFormId] = useState<string>("");
  const queryClient = useQueryClient();
  const { projectClient, currentProject } = useProject();
  const { configuredForms, isLoadingConfigured } = useJotFormForms();

  const handleCreate = async () => {
    try {
      if (!name) {
        throw new Error("Debes especificar un nombre para la plantilla");
      }
      
      if (!selectedFile) {
        throw new Error("Debes seleccionar un archivo PPT/PPTX");
      }

      if (!selectedFormId) {
        throw new Error("Debes seleccionar un formulario de JotForm");
      }

      let pptFilePath = null;
      
      // Si se seleccionó un archivo, súbelo primero
      if (selectedFile) {
        const fileName = `template_${Date.now()}.${selectedFile.name.split('.').pop()}`;
        const filePath = `template_files/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('templates')
          .upload(filePath, selectedFile);

        if (uploadError) throw uploadError;
        
        pptFilePath = filePath;
      }

      // Crear la plantilla en la base de datos
      const { data, error } = await projectClient.from("report_templates_v2").insert({
        name,
        template: "",
        ppt_file_path: pptFilePath,
        canva_template_url: canvaTemplateUrl,
        jotform_form_id: selectedFormId,
        project_id: projectClient.getProjectId()
      }).select();

      if (error) throw error;

      toast.success("Plantilla creada correctamente");
      queryClient.invalidateQueries({ queryKey: ["templates_v2", currentProject] });
      onOpenChange(false);
      setName("");
      setSelectedFile(null);
      setCanvaTemplateUrl("");
      setSelectedFormId("");
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh]">
        <DialogHeader>
          <DialogTitle>Crear Nueva Plantilla</DialogTitle>
          <DialogDescription>
            Crea una nueva plantilla de reporte asociada a un formulario de JotForm
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 flex-1 flex flex-col">
          <div className="space-y-2">
            <Label>Nombre de la plantilla</Label>
            <Input
              placeholder="Nombre de la plantilla"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Formulario de JotForm *</Label>
            <Select value={selectedFormId} onValueChange={setSelectedFormId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un formulario" />
              </SelectTrigger>
              <SelectContent>
                {configuredForms?.map((form) => (
                  <SelectItem key={form.id} value={form.jotform_form_id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{form.form_title || form.jotform_form_id}</span>
                      <div className="flex gap-1 ml-2">
                        {form.is_referral_form ? (
                          <Badge variant="outline" className="text-xs">
                            Referido
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">
                            Principal
                          </Badge>
                        )}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedFormId && (() => {
              const selectedForm = configuredForms?.find(f => f.jotform_form_id === selectedFormId);
              return selectedForm?.is_referral_form && selectedForm?.parent && Array.isArray(selectedForm.parent) && selectedForm.parent.length > 0 ? (
                <div className="text-sm text-muted-foreground mt-2 p-2 bg-muted rounded">
                  <strong>Formulario padre:</strong> {selectedForm.parent[0].form_title || selectedForm.parent[0].jotform_form_id}
                </div>
              ) : null;
            })()}
            {configuredForms?.length === 0 && (
              <p className="text-sm text-amber-600">
                No hay formularios configurados. Por favor, ve a la sección de formularios de JotForm para agregar uno.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Subir archivo PPT/PPTX</Label>
            <Input
              type="file"
              accept=".ppt,.pptx"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setSelectedFile(e.target.files[0]);
                }
              }}
            />
            {selectedFile && (
              <div className="text-sm text-green-500">
                Archivo seleccionado: {selectedFile.name}
              </div>
            )}
          </div>

          {/*<div className="space-y-2">
            <Label>Enlace de plantilla de Canva (opcional)</Label>
            <Input
              placeholder="URL de Canva"
              value={canvaTemplateUrl}
              onChange={(e) => setCanvaTemplateUrl(e.target.value)}
            />
          </div>*/}

          <div className="flex justify-end space-x-2 mt-auto">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={!selectedFormId}>
              Crear
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
